import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  Paper,
  Avatar,
  Chip,
  Divider,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  People,
  Work,
  Assignment,
  VideoCall,
  Assessment,
  Dashboard,
  Settings,
  TrendingUp,
  Schedule,
  Notifications,
  Business,
  School,
  Psychology,
  Engineering,
  Person,
  CalendarToday,
  Timer
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const AdminLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    activeJobs: 0,
    totalApplications: 0,
    pendingReviews: 0,
    scheduledInterviews: 0
  });
  const [loading, setLoading] = useState(true);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchUpcomingInterviews();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const [jobsRes, applicationsRes, interviewsStatsRes] = await Promise.all([
        axios.get('http://localhost:5000/api/jobs', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('http://localhost:5000/api/applications/admin/all', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('http://localhost:5000/api/interviews/stats/overview', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const activeJobs = jobsRes.data.data?.filter(job => job.status === 'active').length || 0;
      const totalApplications = applicationsRes.data.data?.length || 0;
      const pendingReviews = applicationsRes.data.data?.filter(app => 
        app.status === 'submitted' || app.status === 'under_review'
      ).length || 0;
      const scheduledInterviews = interviewsStatsRes.data.data?.upcomingCount || 0;

      setStats({
        activeJobs,
        totalApplications,
        pendingReviews,
        scheduledInterviews
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingInterviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5000/api/interviews?upcoming=true', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUpcomingInterviews(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching upcoming interviews:', error);
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getInterviewTypeColor = (type) => {
    const colors = {
      'technical': 'primary',
      'hr': 'secondary',
      'behavioral': 'info',
      'cultural': 'warning',
      'final': 'success',
      'screening': 'default'
    };
    return colors[type] || 'default';
  };

  const quickActions = [
    {
      title: 'Manage Applications',
      description: 'Review and manage candidate applications',
      icon: <People sx={{ fontSize: 40 }} />,
      color: '#2196f3',
      path: '/admin/applications',
      badge: 'New'
    },
    {
      title: 'Job Management',
      description: 'Create and manage job postings',
      icon: <Work sx={{ fontSize: 40 }} />,
      color: '#4caf50',
      path: '/manage-jobs'
    },
    {
      title: 'Task Management',
      description: 'Create and assign technical assessments',
      icon: <Assignment sx={{ fontSize: 40 }} />,
      color: '#ff9800',
      path: '/tasks'
    },
    {
      title: 'Interview Scheduling',
      description: 'Schedule and manage interviews',
      icon: <VideoCall sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
      path: '/task-submissions'
    },
    {
      title: 'User Management',
      description: 'Manage system users and permissions',
      icon: <Settings sx={{ fontSize: 40 }} />,
      color: '#607d8b',
      path: '/admin/users'
    },
    {
      title: 'Analytics Dashboard',
      description: 'View detailed analytics and reports',
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#e91e63',
      path: '/admin'
    }
  ];

  const statsData = [
    { label: 'Active Jobs', value: stats.activeJobs.toString(), icon: <Work />, color: '#4caf50' },
    { label: 'Total Applications', value: stats.totalApplications.toString(), icon: <People />, color: '#2196f3' },
    { label: 'Pending Reviews', value: stats.pendingReviews.toString(), icon: <Assessment />, color: '#ff9800' },
    { label: 'Scheduled Interviews', value: stats.scheduledInterviews.toString(), icon: <Schedule />, color: '#9c27b0' }
  ];

  // Remove the hardcoded recentActivities array as we'll use upcomingInterviews instead

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Welcome back, {user?.profile?.firstName || 'Administrator'}!
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
              Manage your hiring process efficiently with our comprehensive platform
            </Typography>
          </Box>
          <Avatar
            sx={{ 
              width: 80, 
              height: 80, 
              bgcolor: '#1a237e',
              fontSize: '2rem'
            }}
          >
            {user?.profile?.firstName?.charAt(0) || 'A'}
          </Avatar>
        </Box>
        
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Quick Tip:</strong> Use the quick action cards below to navigate to different sections of the platform.
          </Typography>
        </Alert>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsData.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ 
              height: '100%', 
              background: `linear-gradient(135deg, ${stat.color}15, ${stat.color}25)`,
              border: `1px solid ${stat.color}30`
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: stat.color }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: `${stat.color}20`,
                    color: stat.color
                  }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
        Quick Actions
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickActions.map((action, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                }
              }}
            >
              <CardActionArea 
                onClick={() => navigate(action.path)}
                sx={{ height: '100%', p: 3 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ 
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: 3,
                    bgcolor: `${action.color}15`,
                    color: action.color,
                    mb: 2,
                    position: 'relative'
                  }}>
                    {action.icon}
                    {action.badge && (
                      <Chip
                        label={action.badge}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: '#f44336',
                          color: 'white',
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activities and Platform Overview */}
      <Grid container spacing={4}>
        {/* Recent Activities */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
              Upcoming Scheduled Interviews
            </Typography>
            <Box>
              {upcomingInterviews.length > 0 ? (
                upcomingInterviews.slice(0, 5).map((interview, index) => (
                  <Box key={interview._id || index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ 
                        p: 1, 
                        borderRadius: '50%', 
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        mr: 2,
                        mt: 0.5
                      }}>
                        <VideoCall sx={{ fontSize: 16 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {interview.candidate?.profile?.firstName && interview.candidate?.profile?.lastName 
                              ? `${interview.candidate.profile.firstName} ${interview.candidate.profile.lastName}`
                              : interview.candidate?.email || 'Candidate'}
                          </Typography>
                          <Chip 
                            label={interview.interviewType} 
                            size="small"
                            color={getInterviewTypeColor(interview.interviewType)}
                          />
                        </Box>
                        {interview.job && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            <Work sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            {interview.job.title}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          <CalendarToday sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                          {formatDate(interview.scheduledDate)} at {formatTime(interview.scheduledDate)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Duration: {interview.duration} minutes
                        </Typography>
                      </Box>
                      <Tooltip title="Join Interview">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => window.open(interview.meetingLink, '_blank')}
                          sx={{ mt: 0.5 }}
                        >
                          <VideoCall />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {index < Math.min(upcomingInterviews.length, 5) - 1 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No upcoming interviews scheduled
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/task-submissions')}
                  >
                    Review Task Submissions
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Platform Overview */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
              Platform Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Business sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Job Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create and manage job postings with advanced filtering and analytics
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <School sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Assessment Tools
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Technical assessments and skill evaluations for candidates
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Psychology sx={{ fontSize: 40, color: '#9c27b0', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Interview Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Schedule and conduct interviews with integrated video calling
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Engineering sx={{ fontSize: 40, color: '#607d8b', mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Analytics & Reports
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Comprehensive analytics and reporting for data-driven decisions
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box sx={{ mt: 6, textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          © 2024 Hiring Platform. All rights reserved.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Streamlining the hiring process with modern technology
        </Typography>
      </Box>
    </Container>
  );
};

export default AdminLanding;
